/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印全链路埋点
 */

import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '../../../app/hooks'
import sensors, { sensorsRole } from '../../../utils/sensors'
import {
  activateEntryAnalyticsContext,
  getCurrentAnalyticsContext,
  hasCurrentAnalyticsContext,
  prepareExposureAnalyticsContext
} from './utils/batchEditWatermarkAnalyticsContext'
import type { UploadFolderStrategy } from './utils/uploadFolderStrategy'
import { useBatchWatermarkEntryValidationVariant } from './hooks/useBatchWatermarkEntryValidationVariant'
import { useBatchWatermarkEditorClarityVariant } from './hooks/useBatchWatermarkEditorClarityVariant'

const CURRENT_PAGE_NAME = 'web_batch_edit_watermark'
const TRACK_SIGN_PREFIX = 'aworkgroup'

const trackAnalyticsEvent = (
  eventName: string,
  properties: Record<string, unknown>
): void => {
  try {
    sensors.track(eventName, properties)
  } catch {
    // 埋点 SDK 异常不能阻塞付费、重试、下载等业务流程。
  }
}

export type BatchEditWatermarkFromPlace =
  | 'all_photo'
  | 'folder'
  | 'ai_folder'
  | 'add_photos'
  | 'direct'

export type BatchEditWatermarkPageType =
  | 'allPhoto'
  | 'notEdit'
  | 'edited'
  | 'failEdit'

export type BatchEditWatermarkActionName =
  | 'prepare_editor'
  | 'extract_watermark'
  | 'apply_current_photo'
  | 'apply_selected_photos'
  | 'apply_all_photos'
  | 'download_all_photos'
  | 'download_current_photo'
  | 'upload_to_folder'
  | 'upload_and_delete_original'
  | 'create_folder'

export type BatchEditWatermarkFolderStrategy = UploadFolderStrategy

export interface BatchEditWatermarkFolderAnalytics {
  /** 当前上传操作采用的低基数目标文件夹策略。 */
  folderStrategy: BatchEditWatermarkFolderStrategy
  /** 当前策略是否由历史团队偏好自动预选。 */
  isPreferenceRemembered: boolean
}

interface ExposureParams {
  fromPlace?: BatchEditWatermarkFromPlace
  photoNum: number
}

interface ClickParams {
  clickItem: string
  fromPlace?: BatchEditWatermarkFromPlace
  pageType?: BatchEditWatermarkPageType
  photoNum?: number
  folderStrategy?: BatchEditWatermarkFolderStrategy
  isPreferenceRemembered?: boolean
}

interface EnterParams {
  photoNum: number
  uneditedPhotoNum: number
  editedPhotoNum: number
  failedPhotoNum: number
  pageType?: BatchEditWatermarkPageType
}

export type BatchEditWatermarkEntryValidationResult =
  | 'partial_valid'
  | 'all_invalid'

type EntryValidationDialogClickItem =
  | 'entry_validation_continue_edit'
  | 'entry_validation_add_photos'
  | 'entry_validation_cancel'
  | 'entry_validation_close'
  | 'entry_validation_filter_all'
  | 'entry_validation_filter_editable'
  | 'entry_validation_filter_invalid'
  | 'entry_validation_preview_photo'
  | 'entry_validation_view_support_scope'

interface DialogParams {
  popTitle:
    | 'process_result'
    | 'leave_guard'
    | 'upload_failure'
    | 'entry_validation'
  clickItem?:
    | 'process_result_download'
    | 'process_result_upload'
    | 'process_result_continue'
    | 'process_result_retry'
    | 'leave_guard_stay'
    | 'leave_guard_download'
    | 'leave_guard_upload'
    | 'leave_guard_leave'
    | 'upload_failure_retry'
    | 'upload_failure_download'
    | EntryValidationDialogClickItem
  photoNum: number
  editablePhotoNum?: number
  invalidPhotoNum?: number
  validationResult?: BatchEditWatermarkEntryValidationResult
}

export interface ResultParams {
  actionName: BatchEditWatermarkActionName
  actionResult: 'success' | 'fail'
  costTime: number
  photoNum: number
  successPhotoNum?: number
  failedPhotoNum?: number
  skippedPhotoNum?: number
  timeoutPhotoNum?: number
  errorMsg?: string
  taskID?: string
  pageType?: BatchEditWatermarkPageType
  folderStrategy?: BatchEditWatermarkFolderStrategy
  isPreferenceRemembered?: boolean
}

export interface UseBatchEditWatermarkAnalyticsOptions {
  /** 固定当前调用方的入口来源，后续曝光和点击无需重复传递。 */
  fromPlace?: BatchEditWatermarkFromPlace
}

/**
 * 批量编辑水印全链路埋点 Hook。
 *
 * 统一补充 groupID、role、sessionID、fromPlace，业务侧只传动作和结果。
 *
 * @param options - 可选的入口绑定配置
 * @returns 曝光、点击、进入页面与结果埋点方法
 */
export const useBatchEditWatermarkAnalytics = (
  options: UseBatchEditWatermarkAnalyticsOptions = {}
) => {
  const { fromPlace: boundFromPlace } = options
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const role = sensorsRole(myRole)
  const location = useLocation()
  const viewKey = `${location.key}:${location.pathname}`
  const entryValidationVariant = useBatchWatermarkEntryValidationVariant()
  const editorClarityVariant = useBatchWatermarkEditorClarityVariant()

  const trackExposure = useCallback(
    (params: ExposureParams): void => {
      const fromPlace = params.fromPlace ?? boundFromPlace ?? 'direct'
      const { context, shouldTrack } = prepareExposureAnalyticsContext(
        fromPlace,
        params.photoNum,
        viewKey
      )
      if (!shouldTrack) {
        return
      }
      trackAnalyticsEvent('workgroup_exposure_web_batch_edit_watermark', {
        currentPageName: CURRENT_PAGE_NAME,
        track_sign: `${TRACK_SIGN_PREFIX}.exposure.web_batch_edit_watermark`,
        fromPlace: context.fromPlace,
        sessionID: context.sessionID,
        photoNum: params.photoNum,
        groupID: groupID || '',
        role
      })
    },
    [boundFromPlace, groupID, role, viewKey]
  )

  const trackClick = useCallback(
    (params: ClickParams): void => {
      const fromPlace = params.fromPlace ?? boundFromPlace
      const context = fromPlace
        ? activateEntryAnalyticsContext(fromPlace, params.photoNum, viewKey)
        : getCurrentAnalyticsContext(params.photoNum)
      trackAnalyticsEvent('workgroup_click_web_batch_edit_watermark', {
        currentPageName: CURRENT_PAGE_NAME,
        track_sign: `${TRACK_SIGN_PREFIX}.click.web_batch_edit_watermark`,
        clickItem: params.clickItem,
        fromPlace: context.fromPlace,
        sessionID: context.sessionID,
        pageType: params.pageType || 'allPhoto',
        photoNum: params.photoNum ?? context.photoNum,
        string4: entryValidationVariant,
        string5: editorClarityVariant,
        ...(params.folderStrategy
          ? {
              string2: params.folderStrategy,
              bool1: params.isPreferenceRemembered ?? false
            }
          : {}),
        groupID: groupID || '',
        role
      })
    },
    [
      boundFromPlace,
      entryValidationVariant,
      editorClarityVariant,
      groupID,
      role,
      viewKey
    ]
  )

  const trackEnter = useCallback(
    (params: EnterParams): void => {
      if (!hasCurrentAnalyticsContext()) {
        trackExposure({
          fromPlace: 'direct',
          photoNum: params.photoNum
        })
        trackClick({
          clickItem: 'open_editor',
          fromPlace: 'direct',
          pageType: params.pageType,
          photoNum: params.photoNum
        })
      }

      const context = getCurrentAnalyticsContext(params.photoNum)
      trackAnalyticsEvent('workgroup_enter_web_batch_edit_watermark', {
        currentPageName: CURRENT_PAGE_NAME,
        track_sign: `${TRACK_SIGN_PREFIX}.enter.web_batch_edit_watermark`,
        fromPlace: context.fromPlace,
        sessionID: context.sessionID,
        pageType: params.pageType || 'allPhoto',
        photoNum: params.photoNum,
        number1: params.photoNum,
        number2: params.uneditedPhotoNum,
        number3: params.editedPhotoNum,
        number4: params.failedPhotoNum,
        string4: entryValidationVariant,
        string5: editorClarityVariant,
        groupID: groupID || '',
        role
      })
    },
    [
      entryValidationVariant,
      editorClarityVariant,
      groupID,
      role,
      trackClick,
      trackExposure
    ]
  )

  const trackResult = useCallback(
    (params: ResultParams): void => {
      const context = getCurrentAnalyticsContext(params.photoNum)
      trackAnalyticsEvent('workgroup_result_web_batch_edit_watermark', {
        currentPageName: CURRENT_PAGE_NAME,
        track_sign: `${TRACK_SIGN_PREFIX}.result.web_batch_edit_watermark`,
        actionName: params.actionName,
        actionResult: params.actionResult,
        errorMsg: params.errorMsg || '',
        costTime: Math.max(0, Math.round(params.costTime)),
        fromPlace: context.fromPlace,
        sessionID: context.sessionID,
        pageType: params.pageType || 'allPhoto',
        photoNum: params.photoNum,
        number1: params.successPhotoNum ?? 0,
        number2: params.failedPhotoNum ?? 0,
        number3: params.skippedPhotoNum ?? 0,
        number4: params.timeoutPhotoNum ?? 0,
        string1: params.taskID || '',
        string4: entryValidationVariant,
        string5: editorClarityVariant,
        ...(params.folderStrategy
          ? {
              string2: params.folderStrategy,
              bool1: params.isPreferenceRemembered ?? false
            }
          : {}),
        groupID: groupID || '',
        role
      })
    },
    [editorClarityVariant, entryValidationVariant, groupID, role]
  )

  const trackDialogShow = useCallback(
    (params: DialogParams): void => {
      const context = getCurrentAnalyticsContext(params.photoNum)
      trackAnalyticsEvent('workgroup_dialog_show_web_batch_edit_watermark', {
        currentPageName: CURRENT_PAGE_NAME,
        track_sign: `${TRACK_SIGN_PREFIX}.dialog_show.web_batch_edit_watermark`,
        popTitle: params.popTitle,
        fromPlace: context.fromPlace,
        sessionID: context.sessionID,
        photoNum: params.photoNum,
        number1: params.editablePhotoNum ?? 0,
        number2: params.invalidPhotoNum ?? 0,
        string1: params.validationResult ?? '',
        string4: entryValidationVariant,
        string5: editorClarityVariant,
        groupID: groupID || '',
        role
      })
    },
    [editorClarityVariant, entryValidationVariant, groupID, role]
  )

  const trackDialogClick = useCallback(
    (params: DialogParams): void => {
      if (!params.clickItem) {
        return
      }

      const context = getCurrentAnalyticsContext(params.photoNum)
      trackAnalyticsEvent('workgroup_dialog_click_web_batch_edit_watermark', {
        currentPageName: CURRENT_PAGE_NAME,
        track_sign: `${TRACK_SIGN_PREFIX}.dialog_click.web_batch_edit_watermark`,
        popTitle: params.popTitle,
        clickItem: params.clickItem,
        fromPlace: context.fromPlace,
        sessionID: context.sessionID,
        photoNum: params.photoNum,
        number1: params.editablePhotoNum ?? 0,
        number2: params.invalidPhotoNum ?? 0,
        string1: params.validationResult ?? '',
        string4: entryValidationVariant,
        string5: editorClarityVariant,
        groupID: groupID || '',
        role
      })
    },
    [editorClarityVariant, entryValidationVariant, groupID, role]
  )

  return {
    trackExposure,
    trackClick,
    trackEnter,
    trackResult,
    trackDialogShow,
    trackDialogClick
  }
}

export const getAnalyticsErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'string' && error) {
    return error
  }
  return fallback
}
